import * as React from "react";
import { CalendarIcon, X } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

/**
 * Iter v34{ae} — Drop-in Shadcn date picker replacing native date inputs.
 * Contract:
 *   - value:    ISO date string "YYYY-MM-DD" or empty
 *   - onChange: called with a synthetic event { target: { value } }
 *
 * This lets us swap out native date inputs without refactoring form handlers,
 * because the frontend consistently reads `e.target.value` on onChange.
 */
export function DatePicker({
  value = "",
  onChange,
  min,
  max,
  disabled = false,
  placeholder = "Pilih tanggal",
  className = "",
  "data-testid": testId,
  clearable = true,
}) {
  const [open, setOpen] = React.useState(false);

  const parsed = React.useMemo(() => {
    if (!value) return undefined;
    const d = parse(value, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  }, [value]);

  const label = parsed ? format(parsed, "dd MMM yyyy") : "";

  const emit = (iso) => {
    onChange?.({ target: { value: iso } });
  };

  const minDate = React.useMemo(() => {
    if (!min) return undefined;
    const d = parse(min, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  }, [min]);
  const maxDate = React.useMemo(() => {
    if (!max) return undefined;
    const d = parse(max, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  }, [max]);

  return (
    <Popover open={open} onOpenChange={disabled ? () => {} : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={`inline-flex items-center gap-2 w-full h-10 px-3 border border-stone-300 bg-white text-sm text-left text-stone-800 hover:border-stone-400 focus:outline-none focus:border-terra-500 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        >
          <CalendarIcon className="h-4 w-4 text-stone-500 shrink-0" />
          <span className={`flex-1 truncate ${label ? "" : "text-stone-400"}`}>{label || placeholder}</span>
          {clearable && value && !disabled && (
            <X
              className="h-3.5 w-3.5 text-stone-400 hover:text-stone-700 shrink-0"
              onClick={(e) => { e.stopPropagation(); emit(""); }}
              data-testid={testId ? `${testId}-clear` : undefined}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 z-50 bg-white border border-stone-200 shadow-lg">
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={(d) => {
            if (d && isValid(d)) {
              emit(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          disabled={(d) => {
            if (minDate && d < minDate) return true;
            if (maxDate && d > maxDate) return true;
            return false;
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
